import {Injectable} from '@angular/core';
import {DetentionRegistration} from '@model/detention-registration';
import {Page} from '@model/page';
import {ScheduledDetention} from '@model/scheduled-detention';
import {School} from '@model/school';
import {SearchData} from '@model/search-data';
import {SearchDataStudentDetention} from '@model/search-data-student-detention';
import {SearchDataStudentWithRemarks} from '@model/search-data-student-with-remarks';
import {StaffMember} from '@model/staffMember';
import {StudentDetention} from '@model/student-detention';
import {StudentDetentionAttendance} from '@model/student-detention-attendance';
import {StudentView} from '@model/student-view';
import {StudentsWithRemarks} from '@model/student-with-remarks';
import {select, Store} from '@ngrx/store';
import {
  ScheduledDetentionDeleted,
  ScheduledDetentionSaved,
  StudentDetentionAttendancesLoaded,
  StudentDetentionAttendancesReset,
  StudentDetentionAttendanceUpdated
} from '@redux/actions/detention.action';
import {StudentsWithLateArrivalsLoaded, StudentsWithRemarksLoaded} from '@redux/actions/disciplinary.action';
import {getStudentDetentionAttendances, getStudentDetentions, getStudentsWithLateArrivals, getStudentsWithRemarks, StoreState} from '@redux/reducers';
import {DetentionService} from '@services/detention.service';
import {SchoolService} from '@services/school.service';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {Observable, switchMap, take} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {StudentsWithLateArrivals} from '@model/student-with-late-arrivals';
import {SearchDataStudentWithLateArrivals} from '@model/search-data-student-with-late-arrivals';

@Injectable()
export class DisciplinaryFacadeService {
  school$: Observable<School>;
  isMobile$: Observable<boolean>;
  scheduledDetentions$: Observable<ScheduledDetention[]>;
  staffMembers$: Observable<StaffMember[]>;
  studentDetentions$: Observable<Page<StudentDetention>>;
  studentSearchResult$: Observable<Page<StudentView>>;
  studentDetentionAttendances$: Observable<StudentDetentionAttendance[]>;
  detentionRegistrations$: Observable<DetentionRegistration[]>;
  searchDataStudentDetention$: Observable<SearchDataStudentDetention>;
  studentsWithRemarks$: Observable<Page<StudentsWithRemarks>>;
  studentsWithLateArrivals$: Observable<Page<StudentsWithLateArrivals>>
  activeSchoolYear$: Observable<number>;

  constructor(private _sharedFacade: SharedFacadeService,
              private _detentionService: DetentionService,
              private _schoolService: SchoolService,
              private _store: Store<StoreState>) {
    this.school$ = this._sharedFacade.school$;
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.scheduledDetentions$ = this._sharedFacade.scheduledDetentions$;
    this.staffMembers$ = this._sharedFacade.staffMembers$;
    this.studentDetentions$ = this._store.pipe(select(getStudentDetentions));
    this.studentSearchResult$ = this._sharedFacade.studentSearchResult$;
    this.studentDetentionAttendances$ = this._store.pipe(select(getStudentDetentionAttendances));
    this.detentionRegistrations$ = this._sharedFacade.detentionRegistrations$;
    this.searchDataStudentDetention$ = this._sharedFacade.searchDataStudentDetention$;
    this.studentsWithRemarks$ = this._store.pipe(select(getStudentsWithRemarks));
    this.studentsWithLateArrivals$ = this._store.pipe(select(getStudentsWithLateArrivals))
    this.activeSchoolYear$ = this._sharedFacade.activeSchoolYear$;
  }

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  loadScheduledDetentions() {
    this._sharedFacade.loadScheduledDetentions();
  }

  saveScheduledDetention(scheduledDetention: ScheduledDetention) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.saveScheduledDetention(school.id, scheduledDetention))
    ).subscribe(scheduledDetention => {
      this._store.dispatch(ScheduledDetentionSaved({scheduledDetention: scheduledDetention}));
    });
  }

  deleteScheduledDetention(detentionId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.deleteScheduledDetention(school.id, detentionId))
    ).subscribe(() => {
      this._store.dispatch(ScheduledDetentionDeleted({scheduledDetentionId: detentionId}));
    });
  }

  loadDetentionsBySearchData(searchDataStudentDetention?: SearchDataStudentDetention) {
    this._sharedFacade.loadDetentionsBySearchData(searchDataStudentDetention);
  }

  saveStudentDetention(studentDetention: StudentDetention) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.saveStudentDetention(school.id, studentDetention)),
    ).subscribe(_studentDetention => this.loadDetentionsBySearchData());
  }

  saveStudentDetentionAndRegistration(studentDetention: StudentDetention, detentionId: string, sendEmails: boolean) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.saveStudentDetention(school.id, studentDetention)),
    ).subscribe(_studentDetention => this.saveDetentionRegistration(_studentDetention.id, detentionId, sendEmails));
  }

  saveDetentionRegistration(studentDetentionId: string, detentionId: string, sendEmails: boolean) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.registerStudentDetention(school.id, studentDetentionId, detentionId, sendEmails)),
      catchError(err => {
        this.loadDetentionsBySearchData();
        throw err;
      })
    ).subscribe(_ => {
      this.loadDetentionsBySearchData();
    });
  }

  deleteAndReloadDetentionRegistration(scheduledDetentionId: string, studentDetentionId: string, studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.deleteDetentionRegistration(school.id, scheduledDetentionId, studentDetentionId))
    ).subscribe(_ => {
      this.loadDetentionsBySearchData();
      this.loadDetentionRegistrations(studentId, studentDetentionId);
    });
  }

  deleteStudentDetention(studentId: number, studentDetentionId: string) {
    this._sharedFacade.deleteStudentDetention(studentId, studentDetentionId);
  }

  searchStudents(searchData: SearchData) {
    this._sharedFacade.searchStudents(searchData);
  }

  loadStudentDetentionAttendances(studentDetentionId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.getStudentDetentionAttendances(school.id, studentDetentionId))
    ).subscribe(studentDetentionAttendances => {
      this._store.dispatch(StudentDetentionAttendancesLoaded({studentDetentionAttendances}));
    });
  }

  saveStudentDetentionAttendance(studentDetentionAttendance: StudentDetentionAttendance) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.saveStudentDetentionAttendance(
        school.id,
        studentDetentionAttendance.scheduledDetentionId,
        studentDetentionAttendance.studentDetentionId,
        studentDetentionAttendance.attended))
    ).subscribe(_ => {
      this._store.dispatch(StudentDetentionAttendanceUpdated({studentDetentionAttendance}));
    });
  }

  loadDetentionRegistrations(studentId: number, studentDetentionId: string) {
    this._sharedFacade.loadDetentionRegistrations(studentId, studentDetentionId);
  }

  resetDetentionRegistrations() {
    this._sharedFacade.resetDetentionRegistrations();
  }

  resetStudentDetentionAttendances() {
    this._store.dispatch(StudentDetentionAttendancesReset());
  }

  loadStudentsWithRemarks(searchData?: SearchDataStudentWithRemarks) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getStudentsWithRemarks(school.id, searchData))
    ).subscribe(studentsWithRemarks => {
      this._store.dispatch(StudentsWithRemarksLoaded({studentsWithRemarks}));
    });
  }

  loadStudentsWithLateArrivals(searchData?: SearchDataStudentWithLateArrivals) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getStudentsWithLateArrivals(school.id, searchData))
    ).subscribe(studentsWithLateArrivals => {
      this._store.dispatch(StudentsWithLateArrivalsLoaded({studentsWithLateArrivals}));
    });
  }
}
